import {
    AfterViewInit, Component,
    ElementRef,
    EventEmitter, HostBinding, Input,
    OnDestroy, OnInit,
    Output, ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { CultureService } from '../../services/culture.service';


@Component({
    selector: 'se-time-picker',
    templateUrl: './se-time-picker.component.html',
    styleUrls: ['./se-time-picker.component.scss'],
})
export class SeTimePickerComponent implements OnInit, AfterViewInit, OnDestroy {
    @HostBinding('style.display')
    display = 'flex';

    @Output()
    timePickerKeyDown = new EventEmitter<{ ev: KeyboardEvent; picker: 'hour' | 'minute' }>();

    isOnError: boolean = false;

    @Input()
    setError = false;

    @Output()
    errorStateChange = new EventEmitter<boolean>();

    /* Two Way */
    @Output() timeChange = new EventEmitter();

    timeValue: moment.Moment | null;
    @Input()
    get time() {
        return this.timeValue;
    }
    set time(value) {
        if (this.timeValue != value) {
            if (value && value.isValid()) {
                this.timeValue = value;

                this.defaultTime = this.time && this.time.isValid() ? this.time.format(this.timeFormat) : null;
            } else {
                this.timeValue = moment(null);
                this.defaultTime = null;
            }

            this.checkError();

            this.timeChange.emit(this.timeValue);
        }
    }

    @ViewChild('pickerDiv', { static: false }) datePickerDiv: ElementRef;

    @Input()
    showIcon: boolean = true;

    @Input()
    disabled: boolean = false;

    @Input()
    showResetButton: boolean = true;

    @Input()
    highlightBackgroundEnabled: boolean = false;

    @Input()
    timeFormat: string = 'HH:mm';

    @Input()
    hourPlaceholder: string = 'hh';

    @Input()
    minutePlaceholder: string = 'mm';

    @Input()
    clockAmPm: number = this.getClockFormat(); // 12 per AM PM

    @Input()
    required: boolean = false;

    defaultTime: string;

    @Input()
    isWithLabel: boolean = true;

    @Input()
    label = this.translate.instant('Time');

    constructor(private culture: CultureService, private translate: TranslateService) {}

    ngOnInit(): void {
        if (this.time && this.time.isValid()) {
            this.defaultTime = this.time.format(this.timeFormat);
        } else {
            this.timeValue = moment(null);

            this.checkError();
        }
    }

    ngAfterViewInit(): void {
        try {
            // Imposto i placeholder
            let timePickerInputs = this.datePickerDiv.nativeElement.querySelectorAll('input');

            // Evento KeyDown
            timePickerInputs[0].addEventListener('keydown', this.onHourKeyDown);
            timePickerInputs[1].addEventListener('keydown', this.onMinuteKeyDown);

            timePickerInputs[0].placeholder = this.hourPlaceholder;
            timePickerInputs[1].placeholder = this.minutePlaceholder;
        } catch {}
    }

    ngOnDestroy() {
        // Rimuovo l'eveno per evitare leaks...
        let timePickerInputs = this.datePickerDiv.nativeElement.querySelectorAll('input');
        timePickerInputs[0].removeEventListener('keydown', this.onHourKeyDown);
        timePickerInputs[1].removeEventListener('keydown', this.onMinuteKeyDown);
    }

    private getClockFormat() {
        if (this.culture.getCulture() == 'en-US') return 12;

        return 24;
    }

    private fixTimePickerBug(hour: number, minute: number) {
        try {
            let timePickerInputs = this.datePickerDiv.nativeElement.querySelectorAll('input');
            timePickerInputs[0].value = hour.toString().padStart(2, '0');
            timePickerInputs[1].value = minute.toString().padStart(2, '0');
        } catch {}
    }

    private fixTimePickerBug1() {
        try {
            let timePickerInputs = this.datePickerDiv.nativeElement.querySelectorAll('input');
            timePickerInputs[0].value = null;
            timePickerInputs[1].value = null;
        } catch {}
    }

    private getDateTimeDefault() {
        // Oggi alle 12
        const defaultTime = moment();
        defaultTime.hour(12);
        defaultTime.minute(0);
        defaultTime.second(0);

        return defaultTime.clone();
    }

    private moveFocusOMinute() {
        try {
            let timePickerInputs = this.datePickerDiv.nativeElement.querySelectorAll('input');
            timePickerInputs[1].focus();
        } catch {}
    }

    private setNow() {
        this.defaultTime = moment(Date.now()).format(this.timeFormat);
        this.onTimeChanging(this.defaultTime);
    }

    private checkError() {
        if (this.required) {
            this.isOnError = this.setError || !this.time || !this.time.isValid();
            this.errorStateChange.emit(this.isOnError);
        }
    }

    getErrorMessage(): string {
        return this.translate.instant('MandatoryData');
    }

    onResetDateTime() {
        this.fixTimePickerBug1();

        this.time = moment(null);
    }

    onHourKeyDown = (event: KeyboardEvent): void => {
        this.timePickerKeyDown.emit({
            ev: event,
            picker: 'hour',
        });

        //if (!event.shiftKey && event.key == 'Tab') {
        //    setTimeout(() => {
        //        this.moveFocusOMinute();
        //    });
        //}
        //else
        switch (event.key) {
            case ' ':
                if (!this.time || !this.time.isValid()) {
                    this.setNow();
                }
                break;
            case 'Delete':
                this.onResetDateTime();
                this.fixTimePickerBug1();
                break;
            default:
                break;
        }
    };

    onMinuteKeyDown = (event: KeyboardEvent): void => {
        this.timePickerKeyDown.emit({
            ev: event,
            picker: 'minute',
        });

        switch (event.key) {
            case ' ':
                if (!this.time || !this.time.isValid()) {
                    this.setNow();
                }
                break;
            case 'Delete':
                this.onResetDateTime();
                this.fixTimePickerBug1();
                break;
            default:
                break;
        }
    };

    onTimeChanging(value, fixBug: boolean = true) {
        let newDateTime: moment.Moment;

        if (value) {
            // Se è valido prendo quello impostato altrimenti oggi
            const newDate = this.getDateTimeDefault();

            newDateTime = newDate.clone();

            const newTime = moment(value, this.timeFormat);
            newDateTime.hour(newTime.hour());
            newDateTime.minute(newTime.minute());
            newDateTime.second(newTime.second());

            if (fixBug) this.fixTimePickerBug(newTime.hour(), newTime.minute());
        } else newDateTime = moment(null); // now

        this.time = newDateTime.clone();
    }

    // Funzioni di manipolazione dall'esterno

    focus() {
        try {
            let timePickerInputs = this.datePickerDiv.nativeElement.querySelectorAll('input');
            timePickerInputs[0].focus();
        } catch {}
    }

    select() {
        try {
            let timePickerInputs = this.datePickerDiv.nativeElement.querySelectorAll('input');
            timePickerInputs[0].select();
        } catch {}
    }
}
